import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
  ArrowPathIcon,
  MapPinIcon,
  CameraIcon,
  XMarkIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  ClockIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import {
  attendanceCheckIn,
  attendanceCheckOut,
  getAttendanceCalendar,
  getAttendanceMonthlySummary,
  getAttendanceRecords,
  getTodayAttendance,
  submitOvertimeRequest,
  getMyOvertimeRequests,
  getMyExceptions,
  getLeaveBalances,
  getMyLeaves
} from "../services/employee.service";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const AttendanceDashboard = () => {
  const navigate = useNavigate();

  // 1️⃣ CONDITIONAL SELFIE FLAG (MOCK)
  const showSelfieAfterPunchIn = true;

  // Time & Calendar state
  const [time, setTime] = useState(new Date());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [viewYear, setViewYear] = useState(new Date().getFullYear());

  // Backend state
  const [calendarData, setCalendarData] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState({
    present: 0,
    absent: 0,
    leave: 0,
    half_day: 0,
    total: 0
  });

  // Overtime Request Form State
  const [showOTForm, setShowOTForm] = useState(false);
  const [showOTSuccess, setShowOTSuccess] = useState(false);

  // Overtime backend state
  const [overtimeRequests, setOvertimeRequests] = useState([]);
  const [otLoading, setOtLoading] = useState(false);

  // Exceptions state
  const [exceptions, setExceptions] = useState({
    late_coming: 0,
    early_going: 0,
  });

  // New State for cumulative working hours
  const [totalAccumulatedHours, setTotalAccumulatedHours] = useState(160); // Starting mock value

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch Attendance Data Hook
  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        const emp_code = localStorage.getItem("emp_code");

        // Fetch today's attendance first
        const todayRes = await getTodayAttendance();
        console.log("📅 Today's Attendance:", todayRes);
        
        if (todayRes && todayRes.checked_in) {
          const checkInTime = todayRes.check_in_time;
          const [hours, minutes] = checkInTime.split(':');
          const checkInDate = new Date();
          checkInDate.setHours(parseInt(hours), parseInt(minutes), 0);
          setPunchIn(checkInDate);
          
          if (todayRes.check_out_time && todayRes.check_out_time !== '--:--') {
            const [outHours, outMinutes] = todayRes.check_out_time.split(':');
            const checkOutDate = new Date();
            checkOutDate.setHours(parseInt(outHours), parseInt(outMinutes), 0);
            setPunchOut(checkOutDate);
          }
        }

        const calendarRes = await getAttendanceCalendar({
          year: viewYear,
          month: viewMonth + 1
        });

        const summaryRes = await getAttendanceMonthlySummary({
          year: viewYear,
          month: viewMonth + 1
        });

        console.log("📅 Calendar API:", calendarRes);
        console.log("📊 Summary API:", summaryRes);

        // Calendar
        setCalendarData(Array.isArray(calendarRes) ? calendarRes : []);

        // Monthly summary (array format: [emp_code, month, present, absent, leave, half_day, total])
        const row = (summaryRes || []).find(r => r[0] === emp_code);
        if (row) {
          const present = row[2] || 0;
          const absent = row[3] || 0;
          const leave = row[4] || 0;
          const half_day = row[5] || 0;
          const total = row[6] || 0;

          setMonthlySummary({
            present,
            absent,
            leave,
            half_day,
            total
          });
        }

        // Fetch attendance logs (check-in/check-out records)
        const recordsRes = await getAttendanceRecords(30);
        console.log("📋 Attendance Records:", recordsRes);
        
        if (recordsRes && Array.isArray(recordsRes)) {
          const logs = recordsRes.map(record => ({
            date: record.date,
            checkIn: record.check_in_time,
            checkOut: record.check_out_time,
            status: record.status,
            workHours: record.work_hours,
            workHoursDecimal: record.work_hours ? parseFloat(record.work_hours.split('h')[0]) + parseFloat(record.work_hours.split('h')[1].split('m')[0])/60 : 0,
            source: record.source || 'App',
            is_late: record.is_late || false,
            late_minutes: record.late_minutes || 0,
            is_early_leave: record.is_early_leave || false,
            early_leave_minutes: record.early_leave_minutes || 0
          }));
          setAttendanceLogs(logs);
          
          // Calculate total working hours from actual attendance
          const totalHours = logs.reduce((sum, log) => sum + (log.workHoursDecimal || 0), 0);
          setTotalAccumulatedHours(totalHours);
        }

      } catch (err) {
        console.error("❌ Attendance APIs failed", err);
      }
    };

    fetchAttendanceData();
  }, [viewMonth, viewYear]);

  // Load Overtime Requests from API
  useEffect(() => {
    const fetchMyOvertime = async () => {
      try {
        setOtLoading(true);

        const res = await getMyOvertimeRequests();

        console.log("OT RAW RESPONSE:", res);

        // Axios response safety
        const rows = Array.isArray(res) ? res : [];

        setOvertimeRequests(rows);
      } catch (err) {
        console.error("❌ Failed to load overtime requests", err);
      } finally {
        setOtLoading(false);
      }
    };

    fetchMyOvertime();
  }, []);

  // Load My Exceptions from API
  useEffect(() => {
    const fetchMyExceptions = async () => {
      try {
        const emp_code = localStorage.getItem("emp_code");

        if (!emp_code) return;

        const res = await getMyExceptions({ emp_code });

        console.log("🚨 My Exceptions (Attendance Page):", res.data);

        setExceptions({
          late_coming: res.data?.late_coming ?? 0,
          early_going: res.data?.early_going ?? 0,
        });
      } catch (err) {
        console.error("❌ Failed to load exceptions", err);
      }
    };

    fetchMyExceptions();
  }, []);

  // Fetch Leave Balances and History
  useEffect(() => {
    const fetchLeaveData = async () => {
      try {
        const balancesRes = await getLeaveBalances();
        const leavesRes = await getMyLeaves();

        console.log("📊 Leave Balances:", balancesRes);
        console.log("📋 My Leaves:", leavesRes);

        // Process balances
        const balances = balancesRes?.data || balancesRes || [];
        const leaves = leavesRes?.data || leavesRes || [];

        const leaveMap = {
          CL: 'casual',
          SL: 'sick',
          AL: 'earned',
          EL: 'earned'
        };

        const newBalances = {
          casual: { total: 0, used: 0, pending: 0, history: [], upcoming: [] },
          sick: { total: 0, used: 0, pending: 0, history: [], upcoming: [] },
          earned: { total: 0, used: 0, pending: 0, history: [], upcoming: [] }
        };

        // Map balances
        if (Array.isArray(balances)) {
          balances.forEach(balance => {
            const leaveType = leaveMap[balance.leave_type_code];
            if (leaveType) {
              newBalances[leaveType].total = balance.total_leaves || 0;
              newBalances[leaveType].used = balance.used_leaves || 0;
              newBalances[leaveType].pending = balance.balance || 0;
            }
          });
        }

        // Map leave history and upcoming
        if (Array.isArray(leaves)) {
          const today = new Date();
          leaves.forEach(leave => {
            const leaveType = leaveMap[leave.leave_type_code];
            if (leaveType) {
              const leaveDate = new Date(leave.from_date);
              const days = Math.ceil((new Date(leave.to_date) - new Date(leave.from_date)) / (1000 * 60 * 60 * 24)) + 1;
              
              const leaveEntry = {
                date: leave.from_date,
                days: days,
                status: leave.status || 'Pending',
                reason: leave.reason || 'N/A'
              };

              if (leaveDate < today && (leave.status === 'Approved' || leave.status === 'APPROVED')) {
                newBalances[leaveType].history.push(leaveEntry);
              } else if (leaveDate >= today) {
                newBalances[leaveType].upcoming.push(leaveEntry);
              }
            }
          });
        }

        setLeaveBalances(newBalances);
      } catch (err) {
        console.error("❌ Failed to load leave data", err);
        // Keep default empty values on error
      }
    };

    fetchLeaveData();
  }, []);

  // Punch logic
  const [punchIn, setPunchIn] = useState(null);
  const [punchOut, setPunchOut] = useState(null);
  const [workedHours, setWorkedHours] = useState("00:00:00");
  const [showSelfiePrompt, setShowSelfiePrompt] = useState(false);
  const [showPunchOutSuccess, setShowPunchOutSuccess] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [reportPeriod, setReportPeriod] = useState('monthly');
  const [customDateRange, setCustomDateRange] = useState({ from: '', to: '' });
  const [showAllRecords, setShowAllRecords] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [leaveBalances, setLeaveBalances] = useState({
    casual: { total: 0, used: 0, pending: 0, history: [], upcoming: [] },
    sick: { total: 0, used: 0, pending: 0, history: [], upcoming: [] },
    earned: { total: 0, used: 0, pending: 0, history: [], upcoming: [] }
  });

  useEffect(() => {
    if (!punchIn || punchOut) return;
    const interval = setInterval(() => {
      const diff = new Date() - punchIn;
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff / 60000) % 60);
      const s = Math.floor((diff / 1000) % 60);
      setWorkedHours(
        `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
      );
    }, 1000);
    return () => clearInterval(interval);
  }, [punchIn, punchOut]);

  const handlePunchIn = async () => {
    try {
      setLoading(true);
      setApiError(null);

      // Show optional selfie prompt
      setShowSelfiePrompt(true);
    } catch (err) {
      setApiError("Check-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckInWithoutSelfie = async () => {
    try {
      setLoading(true);
      await attendanceCheckIn();

      const now = new Date();
      setPunchIn(now);
      setPunchOut(null);
      setWorkedHours("00:00:00");
      setStatusMessage(
        `Punched In at ${now.toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit"
        })}`
      );
      setShowSelfiePrompt(false);
    } catch (err) {
      let errorMsg = "";
      
      if (typeof err === 'string') {
        errorMsg = err;
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.message) {
        errorMsg = err.message;
      } else if (err.toString) {
        errorMsg = err.toString();
      }
      
      const errorStr = String(errorMsg).toLowerCase();
      
      if (errorStr.includes("already checked in") || errorStr.includes("already")) {
        setApiError("Already checked in for today.");
      } else if (errorStr.includes("shift not assigned")) {
        setApiError("Shift not assigned. Please contact HR.");
      } else {
        setApiError(errorMsg || "Check-in failed. Please try again.");
      }
      setShowSelfiePrompt(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSelfieCapture = async () => {
    try {
      setLoading(true);
      await attendanceCheckIn();

      const now = new Date();
      setPunchIn(now);
      setPunchOut(null);
      setWorkedHours("00:00:00");
      setStatusMessage(
        `Punched In at ${now.toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit"
        })}`
      );
      setShowSelfiePrompt(false);
    } catch (err) {
      let errorMsg = "";
      
      if (typeof err === 'string') {
        errorMsg = err;
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.message) {
        errorMsg = err.message;
      } else if (err.toString) {
        errorMsg = err.toString();
      }
      
      const errorStr = String(errorMsg).toLowerCase();
      
      if (errorStr.includes("already checked in") || errorStr.includes("already")) {
        setApiError("Already checked in for today.");
      } else if (errorStr.includes("shift not assigned")) {
        setApiError("Shift not assigned. Please contact HR.");
      } else {
        setApiError(errorMsg || "Check-in failed. Please try again.");
      }
      setShowSelfiePrompt(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePunchOut = async () => {
    try {
      setLoading(true);
      setApiError(null);

      await attendanceCheckOut();

      const now = new Date();
      setPunchOut(now);
      setStatusMessage(`Punched Out! Total time today: ${workedHours}.`);
      
      if (punchIn) {
        const sessionHours = (now - punchIn) / 3600000;
        setTotalAccumulatedHours(prev => prev + sessionHours);
      }

      setShowPunchOutSuccess(true);
      setTimeout(() => setShowPunchOutSuccess(false), 5000);
    } catch (err) {
      // The error might be a string or an object
      let errorMsg = "";
      
      if (typeof err === 'string') {
        errorMsg = err;
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.message) {
        errorMsg = err.message;
      } else if (err.toString) {
        errorMsg = err.toString();
      }
      
      const errorStr = String(errorMsg).toLowerCase();
      
      if (errorStr.includes("already checked out") || errorStr.includes("already")) {
        setApiError("Already checked out for today.");
      } else if (errorStr.includes("check-in not found")) {
        setApiError("Please check-in first before checking out.");
      } else {
        setApiError(errorMsg || "Check-out failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Calendar logic with backend mapping
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const buildCalendar = () => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push({ empty: true });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const record = calendarData.find(r => r.date === dateStr);

      let status = "none";
      if (record) {
        const map = {
          P: "present",
          PRESENT: "present",
          A: "absent",
          L: "leave",
          WFH: "present",
          HALF_DAY: "leave"
        };
        status = map[record.status] || "none";
      }

      days.push({ day: d, status });
    }

    return days;
  };

  const handleOTSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        date: e.target.date.value,
        hours: e.target.hours.value,
        reason: e.target.reason.value,
      };

      await submitOvertimeRequest(payload);

      setShowOTForm(false);
      setShowOTSuccess(true);

      // 🔁 reload OT list after submit
      const res = await getMyOvertimeRequests();
      setOvertimeRequests(Array.isArray(res) ? res : []);

      setTimeout(() => setShowOTSuccess(false), 4000);
    } catch (err) {
      console.error("❌ OT submit failed", err);
    }
  };

  const downloadAttendanceReport = () => {
    try {
      const emp_code = localStorage.getItem("emp_code") || "EMPLOYEE";
      const emp_name = localStorage.getItem("user_name") || "Employee";
      const now = new Date();
      let fromDate, toDate, filteredLogs;

      // Calculate date range and filter logs based on period
      switch (reportPeriod) {
        case 'daily':
          fromDate = toDate = now.toISOString().split('T')[0];
          filteredLogs = attendanceLogs.filter(log => log.date === fromDate);
          break;
        case 'weekly':
          const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
          fromDate = weekStart.toISOString().split('T')[0];
          toDate = new Date().toISOString().split('T')[0];
          filteredLogs = attendanceLogs.filter(log => log.date >= fromDate && log.date <= toDate);
          break;
        case 'monthly':
          fromDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
          toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
          filteredLogs = attendanceLogs.filter(log => log.date >= fromDate && log.date <= toDate);
          break;
        case 'quarterly':
          const quarter = Math.floor(now.getMonth() / 3);
          fromDate = new Date(now.getFullYear(), quarter * 3, 1).toISOString().split('T')[0];
          toDate = new Date(now.getFullYear(), quarter * 3 + 3, 0).toISOString().split('T')[0];
          filteredLogs = attendanceLogs.filter(log => log.date >= fromDate && log.date <= toDate);
          break;
        case 'halfyearly':
          const half = now.getMonth() < 6 ? 0 : 6;
          fromDate = new Date(now.getFullYear(), half, 1).toISOString().split('T')[0];
          toDate = new Date(now.getFullYear(), half + 6, 0).toISOString().split('T')[0];
          filteredLogs = attendanceLogs.filter(log => log.date >= fromDate && log.date <= toDate);
          break;
        case 'yearly':
          fromDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
          toDate = new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0];
          filteredLogs = attendanceLogs.filter(log => log.date >= fromDate && log.date <= toDate);
          break;
        case 'custom':
          fromDate = customDateRange.from;
          toDate = customDateRange.to;
          filteredLogs = attendanceLogs.filter(log => log.date >= fromDate && log.date <= toDate);
          break;
        default:
          fromDate = toDate = now.toISOString().split('T')[0];
          filteredLogs = attendanceLogs;
      }

      // Create data rows from filtered logs
      const dataRows = filteredLogs.map(log => {
        const otRecord = overtimeRequests.find(ot => (ot.date || ot[2]) === log.date);
        const otHours = otRecord ? (otRecord.hours || otRecord[3] || 0) : 0;
        const extraHoursDisplay = otHours > 0 ? `${otHours} hours` : 'N/A';
        const otStatus = otRecord ? (otRecord.status || otRecord[5] || 'N/A') : 'N/A';

        return {
          'Date': log.date,
          'Check-In': log.checkIn,
          'Check-Out': log.checkOut,
          'Work Hours': log.workHours,
          'Extra Hours': extraHoursDisplay,
          'Extra Hours Status': otStatus,
          'Status': log.status === 'P' || log.status === 'PRESENT' ? 'Present' : log.status === 'A' ? 'Absent' : log.status
        };
      });

      // Create workbook with proper formatting
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([
        ['ATTENDANCE REPORT'],
        [],
        ['Employee Code:', emp_code],
        ['Employee Name:', emp_name],
        ['Report Period:', reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1)],
        ['Date Range:', `${fromDate} to ${toDate}`],
        ['Total Records:', filteredLogs.length],
        ['Generated On:', new Date().toLocaleString('en-IN')],
        [],
        ['Date', 'Check-In', 'Check-Out', 'Work Hours', 'Extra Hours', 'Extra Hours Status', 'Status'],
        ...dataRows.map(row => [
          row['Date'],
          row['Check-In'],
          row['Check-Out'],
          row['Work Hours'],
          row['Extra Hours'],
          row['Extra Hours Status'],
          row['Status']
        ])
      ]);

      // Set column widths
      ws['!cols'] = [
        { wch: 12 },  // Date
        { wch: 10 },  // Check-In
        { wch: 10 },  // Check-Out
        { wch: 12 },  // Work Hours
        { wch: 15 },  // Extra Hours
        { wch: 18 },  // Extra Hours Status
        { wch: 10 }   // Status
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Attendance Report');
      XLSX.writeFile(wb, `Attendance_Report_${reportPeriod}_${new Date().getTime()}.xlsx`);
    } catch (err) {
      console.error('Download error:', err);
      alert('Download failed');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "present":
        return <CheckCircleIcon className="h-2.5 w-2.5 text-green-600" />;
      case "absent":
        return <XMarkIcon className="h-2.5 w-2.5 text-red-600" />;
      case "leave":
        return <DocumentTextIcon className="h-2.5 w-2.5 text-yellow-600" />;
      default:
        return null;
    }
  };

  // Quick actions - Left sidebar
  const quickActions = [
    { label: "Live Tracking", icon: UserGroupIcon, path: "/dashboard/live-tracking" },
    { label: "Field Work", icon: BriefcaseIcon, path: "/dashboard/field-work" },
    { label: "Attendance Correction", icon: ArrowPathIcon, path: "/dashboard/AttendanceCorrectionRequest" },
  ];

  // Charts data - Hooked to backend summary
  const myAttendanceData = {
    labels: ['Present', 'Absent', 'Leave', 'Half Day'],
    datasets: [{
      data: [
        monthlySummary.present,
        monthlySummary.absent,
        monthlySummary.leave,
        monthlySummary.half_day
      ],
      backgroundColor: ['rgba(75, 192, 192, 0.7)', 'rgba(255, 99, 132, 0.7)', 'rgba(255, 206, 86, 0.7)', 'rgba(153, 102, 255, 0.7)'],
      hoverBackgroundColor: ['rgba(90, 210, 210, 1)', 'rgba(255, 99, 132, 1)', 'rgba(255, 206, 86, 1)', 'rgba(153, 102, 255, 1)'],
    }],
  };

  // Calculate check-in times data from actual attendance logs
  const last7Days = attendanceLogs.slice(0, 7).reverse();
  const onTimeData = {
    labels: last7Days.map(log => {
      const d = new Date(log.date);
      return `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })}`;
    }),
    datasets: [{
      label: 'Minutes',
      data: last7Days.map(log => {
        // Positive = late, Negative = early
        return log.is_late ? log.late_minutes : (log.is_early_leave ? -log.early_leave_minutes : 0);
      }),
      backgroundColor: (context) => {
        const value = context.dataset.data[context.dataIndex];
        return value > 0 ? 'rgba(255, 99, 132, 0.7)' : value < 0 ? 'rgba(54, 162, 235, 0.7)' : 'rgba(75, 192, 192, 0.7)';
      },
      borderColor: (context) => {
        const value = context.dataset.data[context.dataIndex];
        return value > 0 ? 'rgba(255, 99, 132, 1)' : value < 0 ? 'rgba(54, 162, 235, 1)' : 'rgba(75, 192, 192, 1)';
      },
      borderWidth: 1,
    }],
  };

  const onTimeOptions = {
    scales: { y: { beginAtZero: true, min: -15, max: 15, title: { display: true, text: 'Minutes' } } },
    plugins: { legend: { display: false } },
    responsive: true,
    maintainAspectRatio: false,
  };

  // Calculate overtime data from actual overtime requests
  const overtimeData = {
    labels: last7Days.map(log => {
      const d = new Date(log.date);
      return `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })}`;
    }),
    datasets: [{
      label: 'Hours',
      data: last7Days.map(log => {
        const otRecord = overtimeRequests.find(ot => (ot.date || ot[2]) === log.date);
        return otRecord ? (otRecord.hours || otRecord[3] || 0) : 0;
      }),
      backgroundColor: 'rgba(75, 192, 192, 0.7)',
      borderColor: 'rgba(75, 192, 192, 1)',
      borderWidth: 1,
    }],
  };

  const overtimeOptions = {
    scales: { y: { beginAtZero: true, max: 5, title: { display: true, text: 'Hours' } } },
    plugins: { legend: { display: false } },
    responsive: true,
    maintainAspectRatio: false,
  };

  // Calculate attendance sources from actual attendance logs
  const sourceCount = attendanceLogs.reduce((acc, log) => {
    const source = log.source || 'App';
    acc[source] = (acc[source] || 0) + 1;
    return acc;
  }, {});
  
  const attendanceSourceData = {
    labels: Object.keys(sourceCount).length > 0 ? Object.keys(sourceCount) : ['App'],
    datasets: [{
      data: Object.keys(sourceCount).length > 0 ? Object.values(sourceCount) : [attendanceLogs.length],
      backgroundColor: ['rgba(255, 206, 86, 0.7)', 'rgba(54, 162, 235, 0.7)', 'rgba(153, 102, 255, 0.7)'],
      hoverBackgroundColor: ['rgba(255, 206, 86, 1)', 'rgba(54, 162, 235, 1)', 'rgba(153, 102, 255, 1)'],
    }],
  };

  const attendanceSourceOptions = {
    plugins: { legend: { position: 'bottom' } },
    responsive: true,
    maintainAspectRatio: false,
  };

  const myAttendanceOptions = {
    plugins: {
      legend: { position: 'bottom' },
      title: { display: true, text: 'My Attendance' },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  const weeklyTrendData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    datasets: [{
      data: [95, 88, 100, 92, 96],
      backgroundColor: [
        'rgba(34, 197, 94, 0.75)',
        'rgba(59, 130, 246, 0.75)',
        'rgba(234, 179, 8, 0.75)',
        'rgba(249, 115, 22, 0.75)',
        'rgba(139, 92, 246, 0.75)',
      ],
      borderWidth: 1,
    }],
  };

  const weeklyTrendOptions = {
    plugins: {
      legend: { display: false },
      title: { display: true, text: 'Weekly %', font: { size: 13 } },
    },
    cutout: '70%',
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] p-4 sm:p-6 font-['Poppins','Roboto','sans-serif'] text-slate-700"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        {/* Attendance Calendar */}
        <div className="lg:col-span-8 bg-white rounded-xl border shadow-md p-4">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-3 gap-3">
            <h3 className="text-base font-semibold">Attendance Calendar</h3>
            <div className="flex gap-3">
              <select
                value={viewMonth}
                onChange={(e) => setViewMonth(Number(e.target.value))}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const isDisabled = viewYear === currentYear && i > currentMonth;
                  return (
                    <option key={i} value={i} disabled={isDisabled}>
                      {new Date(0, i).toLocaleString("default", { month: "long" })}
                    </option>
                  );
                })}
              </select>
              <select
                value={viewYear}
                onChange={(e) => setViewYear(Number(e.target.value))}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm"
              >
                {Array.from({ length: 6 }, (_, i) => currentYear - 5 + i).map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1 text-xs">
            {buildCalendar().length > 0 ? buildCalendar().map((d, i) =>
              d.empty ? (
                <div key={i} />
              ) : (
                <div
                  key={i}
                  className={`p-1.5 rounded border text-center font-medium ${d.status === "present"
                    ? "bg-green-50 text-green-700 border-green-200"
                    : d.status === "absent"
                      ? "bg-red-50 text-red-600 border-red-200"
                      : d.status === "leave"
                        ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                        : "bg-slate-50 text-slate-400 border-slate-200"
                    }`}
                >
                  {d.day}
                  <div className="flex justify-center mt-0.5">
                    {getStatusIcon(d.status)}
                  </div>
                </div>
              )
            ) : (
              <div className="col-span-7 py-10 text-center text-slate-400 italic">
                No data available
              </div>
            )}
          </div>

          {/* Color Legend */}
          <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-4 justify-center">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-green-50 border border-green-200"></div>
              <span className="text-xs text-slate-600">Present</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-red-50 border border-red-200"></div>
              <span className="text-xs text-slate-600">Absent</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-yellow-50 border border-yellow-200"></div>
              <span className="text-xs text-slate-600">Leave/Half Day</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-slate-50 border border-slate-200"></div>
              <span className="text-xs text-slate-600">No Record</span>
            </div>
          </div>
        </div>

        {/* Today's Punch */}
        <div className="lg:col-span-4 flex justify-center">
          <div className="bg-white rounded-xl border shadow-md p-3 w-full sm:w-72">
            <h3 className="text-sm font-semibold mb-2 text-center">
              Today's Punch
            </h3>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-slate-50 p-2 rounded text-center">
                <p className="text-xs text-slate-500">IN</p>
                <p className="text-base font-bold">
                  {punchIn
                    ? punchIn.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                    : "--:--"}
                </p>
              </div>
              <div className="bg-slate-50 p-2 rounded text-center">
                <p className="text-xs text-slate-500">OUT</p>
                <p className="text-base font-bold">
                  {punchOut
                    ? punchOut.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                    : "--:--"}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handlePunchIn}
                disabled={loading}
                className="flex-1 bg-green-600 text-white py-1.5 text-xs rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Processing..." : "Check In"}
              </button>
              <button
                onClick={handlePunchOut}
                disabled={punchOut || loading}
                className="flex-1 bg-red-600 text-white py-1.5 text-xs rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Processing..." : "Check Out"}
              </button>
            </div>
            {apiError && (
              <div className="mt-3 text-sm text-red-600">
                {apiError}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 sm:gap-6">
        {/* LEFT SIDEBAR - Quick Actions */}
        <div className="col-span-12 lg:col-span-3">
          <div className="bg-white rounded-xl border shadow-md p-4">
            <h3 className="text-sm font-semibold mb-4 text-slate-800">Quick Actions</h3>
            <div className="space-y-2.5">
              {quickActions.map((item, i) => (
                <button
                  key={i}
                  onClick={() => navigate(item.path)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-200"
                >
                  <item.icon className="h-5 w-5 text-indigo-600" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl border shadow-md p-4 mt-4 h-52">
            <Doughnut data={weeklyTrendData} options={weeklyTrendOptions} />
          </div>

          <div className="bg-white rounded-xl border shadow-md p-4 mt-4">
            <h3 className="text-sm font-semibold mb-4 text-slate-800 flex items-center gap-2">
              <ClockIcon className="h-5 w-5 text-orange-500" />
              Working Hours Summary
            </h3>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-slate-500">Days Worked This Month</span>
                <span className="text-sm font-bold text-slate-700">{attendanceLogs.filter(log => log.checkOut !== '--:--').length} Days</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-slate-500">Hours Worked This Month</span>
                <span className="text-sm font-bold text-blue-600">{totalAccumulatedHours.toFixed(1)} Hrs</span>
              </div>
            </div>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="col-span-12 lg:col-span-9 space-y-6">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 bg-white p-4 rounded-xl border shadow-md">
              <h3 className="text-sm font-semibold mb-3 text-center">My Attendance Summary</h3>
              <div className="h-40">
                <Doughnut data={myAttendanceData} options={myAttendanceOptions} />
              </div>
              <p className="text-center mt-3 text-base font-bold text-blue-600">
                {monthlySummary.total > 0 ? Math.round((monthlySummary.present / monthlySummary.total) * 100) : 0}% 
                <span className="text-slate-400 font-normal ml-1">Overall Presence</span>
              </p>
            </div>
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-xl border shadow-md">
              <h3 className="text-sm font-semibold mb-3">My Check In Times</h3>
              <div className="h-40">
                <Bar data={onTimeData} options={onTimeOptions} />
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border shadow-md">
              <h3 className="text-sm font-semibold mb-3">My Overtime (Hours)</h3>
              <div className="h-40">
                <Bar data={overtimeData} options={overtimeOptions} />
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border shadow-md">
              <h3 className="text-sm font-semibold mb-3">Attendance Sources</h3>
              <div className="h-40">
                <Doughnut data={attendanceSourceData} options={attendanceSourceOptions} />
              </div>
            </div>
          </div>

          {/* Combined Attendance Records Section */}
          <div className="bg-white rounded-xl border shadow-md p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <ClockIcon className="h-5 w-5 text-indigo-600" />
                My Attendance Records {!showAllRecords && '(Last 7 Days)'}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAllRecords(!showAllRecords)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-semibold"
                >
                  {showAllRecords ? 'Show Last 7 Days' : 'View All'}
                </button>
                <select
                  value={reportPeriod}
                  onChange={(e) => setReportPeriod(e.target.value)}
                  className="border rounded px-2 py-1 text-xs"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="halfyearly">Half Yearly</option>
                  <option value="yearly">Yearly</option>
                  <option value="custom">Custom Range</option>
                </select>
                <button
                  onClick={() => downloadAttendanceReport()}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-semibold flex items-center gap-1"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Download Excel
                </button>
              </div>
            </div>

            {reportPeriod === 'custom' && (
              <div className="flex gap-2 mb-4 bg-slate-50 p-3 rounded">
                <input
                  type="date"
                  value={customDateRange.from}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className="border rounded px-2 py-1 text-xs"
                  placeholder="From"
                />
                <span className="text-xs self-center">to</span>
                <input
                  type="date"
                  value={customDateRange.to}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className="border rounded px-2 py-1 text-xs"
                  placeholder="To"
                />
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-2 text-left border">Date</th>
                    <th className="p-2 text-left border">Check-In</th>
                    <th className="p-2 text-left border">Check-Out</th>
                    <th className="p-2 text-left border">Work Hours</th>
                    <th className="p-2 text-left border">Extra Hours</th>
                    <th className="p-2 text-left border">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceLogs.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-slate-400">No attendance records found.</td>
                    </tr>
                  ) : (
                    (showAllRecords ? attendanceLogs : attendanceLogs.slice(0, 7)).map((log, i) => {
                      const otRecord = overtimeRequests.find(ot => (ot.date || ot[2]) === log.date);
                      const otHours = otRecord ? (otRecord.hours || otRecord[3] || 0) : 0;
                      
                      return (
                        <tr key={i} className="border-t hover:bg-slate-50">
                          <td className="p-2 font-medium border">{log.date}</td>
                          <td className="p-2 border">
                            <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-1 rounded text-xs font-semibold">
                              <CheckCircleIcon className="h-3 w-3" />
                              {log.checkIn}
                            </span>
                          </td>
                          <td className="p-2 border">
                            {log.checkOut !== '--:--' ? (
                              <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 px-2 py-1 rounded text-xs font-semibold">
                                <ArrowRightOnRectangleIcon className="h-3 w-3" />
                                {log.checkOut}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs">Not checked out</span>
                            )}
                          </td>
                          <td className="p-2 font-semibold text-indigo-600 border">{log.workHours}</td>
                          <td className="p-2 font-semibold text-orange-600 border">{otHours > 0 ? `${otHours}h` : 'N/A'}</td>
                          <td className="p-2 border">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              log.status === 'P' || log.status === 'PRESENT' 
                                ? 'bg-green-100 text-green-700' 
                                : log.status === 'A' 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {log.status === 'P' || log.status === 'PRESENT' ? 'Present' : log.status === 'A' ? 'Absent' : log.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Leave Balance & Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-xl border shadow-md lg:col-span-1">
              <h3 className="text-sm font-semibold mb-3">Leave Balance</h3>
              <div className="space-y-3">
                <div 
                  onClick={() => setSelectedLeaveType('casual')}
                  className="flex justify-between items-center bg-slate-50 p-2 rounded-lg px-4 cursor-pointer hover:bg-indigo-50 transition"
                >
                  <p className="text-xs text-slate-500">Casual Leave</p>
                  <p className="text-base font-bold text-indigo-600">{leaveBalances.casual.pending}</p>
                </div>
                <div 
                  onClick={() => setSelectedLeaveType('sick')}
                  className="flex justify-between items-center bg-slate-50 p-2 rounded-lg px-4 cursor-pointer hover:bg-red-50 transition"
                >
                  <p className="text-xs text-slate-500">Sick Leave</p>
                  <p className="text-base font-bold text-red-500">{leaveBalances.sick.pending}</p>
                </div>
                <div 
                  onClick={() => setSelectedLeaveType('earned')}
                  className="flex justify-between items-center bg-slate-50 p-2 rounded-lg px-4 cursor-pointer hover:bg-green-50 transition"
                >
                  <p className="text-xs text-slate-500">Earned Leave</p>
                  <p className="text-base font-bold text-green-600">{leaveBalances.earned.pending}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border shadow-md lg:col-span-1">
              <h3 className="text-sm font-semibold mb-3">My Exceptions</h3>
              <div className="grid grid-cols-1 gap-2">
                <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg px-4">
                  <p className="text-xs text-slate-500">Late Coming</p>
                  <p className="text-base font-bold">{exceptions.late_coming}</p>
                </div>
                <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg px-4">
                  <p className="text-xs text-slate-500">Early Going</p>
                  <p className="text-base font-bold">{exceptions.early_going}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border shadow-md lg:col-span-1">
              <h3 className="text-sm font-semibold mb-3">Pending Requests</h3>
              <div className="grid grid-cols-1 gap-2">
                <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg px-4">
                  <p className="text-xs text-slate-500">Regularization</p>
                  <p className="text-base font-bold">0</p>
                </div>
                <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg px-4">
                  <p className="text-xs text-slate-500">Leaves</p>
                  <p className="text-base font-bold">1</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popups */}
      <AnimatePresence>
        {showPunchOutSuccess && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            className="fixed top-6 left-1/2 -translate-x-1/2 bg-white p-4 rounded-xl shadow-2xl border flex items-center gap-3 z-50"
          >
            <CheckCircleIcon className="h-7 w-7 text-green-600" />
            <div>
              <p className="font-bold">Checked Out Successfully</p>
              <p className="text-sm text-slate-500">Duration: {workedHours}</p>
            </div>
          </motion.div>
        )}

        {showSelfiePrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-2xl w-full max-w-md p-6 relative shadow-xl"
            >
              <button
                onClick={() => {
                  setShowSelfiePrompt(false);
                  setLoading(false);
                }}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CameraIcon className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Attendance Selfie (Optional)</h3>
                <p className="text-sm text-slate-600 mb-6">
                  You can capture a selfie for attendance verification or skip and check-in directly.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleCheckInWithoutSelfie}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm font-semibold hover:bg-slate-50"
                  >
                    Skip & Check In
                  </button>
                  <button
                    onClick={() => {
                      setShowSelfiePrompt(false);
                      navigate('/dashboard/attendance-selfie');
                    }}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 flex items-center justify-center gap-2"
                  >
                    <CameraIcon className="w-4 h-4" />
                    Take Selfie
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {selectedLeaveType && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
            onClick={() => setSelectedLeaveType(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-2xl w-full max-w-2xl p-6 relative shadow-xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedLeaveType(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              <h3 className="text-xl font-bold text-slate-900 mb-4 capitalize">
                {selectedLeaveType} Leave Details
              </h3>
              
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <span className="text-xs text-slate-600 block mb-1">Total Allocated</span>
                  <span className="text-lg font-bold text-slate-900">{leaveBalances[selectedLeaveType].total}</span>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <span className="text-xs text-red-600 block mb-1">Total Used</span>
                  <span className="text-lg font-bold text-red-600">{leaveBalances[selectedLeaveType].used}</span>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <span className="text-xs text-green-600 block mb-1">Pending Balance</span>
                  <span className="text-lg font-bold text-green-600">{leaveBalances[selectedLeaveType].pending}</span>
                </div>
              </div>

              {/* Upcoming/Pending Leaves */}
              {leaveBalances[selectedLeaveType].upcoming.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-orange-500" />
                    Upcoming / Pending Requests
                  </h4>
                  <div className="space-y-2">
                    {leaveBalances[selectedLeaveType].upcoming.map((leave, i) => (
                      <div key={i} className="flex justify-between items-center p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{leave.date}</p>
                          <p className="text-xs text-slate-600">{leave.reason}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs font-bold text-orange-600 bg-orange-100 px-2 py-1 rounded">{leave.status}</span>
                          <p className="text-xs text-slate-500 mt-1">{leave.days} day(s)</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leave History */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <DocumentTextIcon className="h-4 w-4 text-blue-500" />
                  Leave History
                </h4>
                <div className="space-y-2">
                  {leaveBalances[selectedLeaveType].history.length > 0 ? (
                    leaveBalances[selectedLeaveType].history.map((leave, i) => (
                      <div key={i} className="flex justify-between items-center p-3 bg-slate-50 border border-slate-200 rounded-lg">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{leave.date}</p>
                          <p className="text-xs text-slate-600">{leave.reason}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded">{leave.status}</span>
                          <p className="text-xs text-slate-500 mt-1">{leave.days} day(s)</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">No leave history</p>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AttendanceDashboard;